import React, { useState, useEffect } from 'react';
import { Plus, List, Grid, X, Building2, MapPin, Calendar, Eye } from 'lucide-react';
import { useViewMode } from '../../hooks/useViewMode';
import { useNavigation } from '../../contexts/NavigationContext';
import { Account } from '../../types';
import { GoogleAddressAutocomplete, AddressDetails } from '../common/GoogleAddressAutocomplete';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';
import { ViewToggle } from '../common/ViewToggle';

interface AccountsManagementProps {
  onClose?: () => void;
  mode?: 'modal' | 'page';
  initialAccountId?: string; // If provided, show detail view for this account
}

export const AccountsManagement: React.FC<AccountsManagementProps> = ({ onClose, mode = 'page', initialAccountId }) => {
  const { t } = useTranslation();
  const [view, setView] = useState<'list' | 'add-edit' | 'detail'>('list');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const { viewMode, setViewMode, isMobile } = useViewMode();
  const { navigate, currentPath } = useNavigation();
  
  // Read URL query parameters
  useEffect(() => {
    if (mode === 'page' && currentPath.startsWith('/accounts')) {
      const urlParams = new URLSearchParams(window.location.search);
      console.log('[AccountsManagement] Reading URL parameters:', Object.fromEntries(urlParams.entries()));
      // Add any accounts management-specific URL parameter handling here
    }
  }, [currentPath, mode]);
  
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [addressFormatted, setAddressFormatted] = useState('');
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (initialAccountId && accounts.length > 0) {
      const account = accounts.find(a => a._id === initialAccountId);
      if (account) {
        handleViewDetail(account);
      }
    }
  }, [initialAccountId, accounts]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        const accountsArray = Array.isArray(data.data) ? data.data : [data.data];
        setAccounts(accountsArray);
      } else {
        setMessage(data.message || 'Failed to fetch accounts');
      }
    } catch (error) {
      setMessage('Failed to fetch accounts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetail = (account: Account) => {
    setSelectedAccount(account);
    setView('detail');
  };

  const handleBackToList = () => {
    // Navigate to Admin → Accounts tab
    navigate('/admin?tab=accounts');
  };

  const handleAddressSelect = (details: AddressDetails) => {
    setAddressFormatted(details.formatted);
    setAddressDetails(details);
  };

  const resetForm = () => {
    setCompanyName('');
    setAddressFormatted('');
    setAddressDetails(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          companyName,
          address: addressDetails ? {
            formatted: addressDetails.formatted,
            streetNumber: addressDetails.streetNumber,
            streetName: addressDetails.streetName,
            street: addressDetails.street,
            city: addressDetails.city,
            state: addressDetails.state,
            postalCode: addressDetails.postalCode,
            country: addressDetails.country,
            lat: addressDetails.lat,
            lng: addressDetails.lng
          } : {
            formatted: addressFormatted
          }
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Account created successfully');
        resetForm();
        setView('list');
        fetchAccounts();
      } else {
        setMessage(data.message || 'Failed to create account');
      }
    } catch (error) {
      console.error('Failed to create account:', error);
      setMessage('Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('admin.companyName') || 'Company Name'} *
        </label>
        <input
          type="text"
          id="companyName"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          <div className="flex items-center space-x-2">
            <MapPin className="w-5 h-5" />
            <span>{t('admin.address') || 'Address'} *</span>
          </div>
        </label>
        <GoogleAddressAutocomplete
          value={addressFormatted}
          onChange={setAddressFormatted}
          onPlaceSelect={handleAddressSelect}
          placeholder={t('admin.streetAddressPlaceholder') || 'Enter address'}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          id="address-autocomplete"
          name="address"
          required
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={() => {
            setView('list');
            resetForm();
          }}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          {t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (t('common.creating') || 'Creating...') : (t('common.create') || 'Create Account')}
        </button>
      </div>
    </form>
  );

  const formatDate = (dateString: Date | string) => {
    return formatDateUtil(dateString);
  };

  const renderDetailView = () => {
    if (!selectedAccount) return null;

    return (
      <div>
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={handleBackToList}
            className="text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 text-sm flex items-center space-x-1"
          >
            <span>← Back to Admin - Accounts</span>
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center">
              <div className="w-16 h-16 rounded-lg bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 flex items-center justify-center mr-4">
                <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedAccount.companyName}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Account ID: {selectedAccount._id}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success-100 dark:bg-gray-700 dark:border dark:border-success-800/50 text-success-700 dark:text-success-400">
              Active
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Address</h3>
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-gray-900 dark:text-gray-100">{selectedAccount.address.formatted}</p>
                  {selectedAccount.address.street && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedAccount.address.street}</p>
                  )}
                  {(selectedAccount.address.city || selectedAccount.address.state || selectedAccount.address.postalCode) && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {[selectedAccount.address.city, selectedAccount.address.state, selectedAccount.address.postalCode]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                  {selectedAccount.address.country && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedAccount.address.country}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Created</h3>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900 dark:text-gray-100">{formatDate(selectedAccount.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const containerClasses = mode === 'page'
    ? 'p-6 max-w-7xl mx-auto'
    : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

  const contentClasses = mode === 'page'
    ? 'w-full'
    : 'bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto';

  if (view === 'detail') {
    return (
      <div className={containerClasses}>
        <div className={contentClasses}>
          {renderDetailView()}
        </div>
      </div>
    );
  }

  if (view === 'add-edit') {
    return (
      <div className={containerClasses}>
        <div className={contentClasses}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.createAccount') || 'Create Account'}
            </h3>
            <button
              onClick={() => {
                setView('list');
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {renderForm()}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className={contentClasses}>
        {mode !== 'page' && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Accounts
            </h3>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total Accounts: {accounts.length}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setView('add-edit')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>{t('admin.createAccount') || 'Create Account'}</span>
            </button>
            {!isMobile && (
              <ViewToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                options={[
                  { value: 'list', icon: List, title: 'List view' },
                  { value: 'cards', icon: Grid, title: 'Card view' }
                ]}
                iconSize={16}
              />
            )}
          </div>
        </div>

        {message && (
          <div className={`p-3 rounded-md text-sm mb-4 ${
            message.includes('success')
              ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
              : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
          }`}>
            {message}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Loading accounts...</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">Address</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">Created</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr 
                    key={account._id} 
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleViewDetail(account)}
                  >
                    <td className="py-3 px-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {account.companyName}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">
                      {account.address.formatted}
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(account.createdAt)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetail(account);
                        }}
                        className="text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 text-sm inline-flex items-center space-x-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {accounts.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No accounts found
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => (
              <div
                key={account._id}
                className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewDetail(account)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {account.companyName}
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="truncate">{account.address.formatted}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{formatDate(account.createdAt)}</span>
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetail(account);
                  }}
                  className="w-full px-3 py-2 text-sm bg-accent-600 text-white rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center space-x-1"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Details</span>
                </button>
              </div>
            ))}
            
            {accounts.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                No accounts found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

